// @flow

import * as React from 'react';
import styled from 'styled-components';
import { Card, Icon } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { useHistory } from 'react-router-dom';

type TileVisualT = {
  icon: string,
  iconSize: number,
  iconColor: string,
  backgroundColorVar: string,
  textColorVar: string,
};

type Props = {
  label: string,
  tooltip: string,
  to: string | (() => Promise<string> | string),
  defaultState: TileVisualT,
  hoverState: TileVisualT,
};

const TileCard = styled(Card)`
  &&& {
    border-radius: 22px;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    user-select: none;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-sizing: border-box;
  }

  &&&:focus {
    box-shadow: 0 0 0 2px var(--primary-hover);
  }
`;

const TileInner = styled.div`
  width: 100%;
  height: 100%;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-sizing: border-box;
  overflow: hidden;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
  transition: transform 120ms ease;
  will-change: transform;
`;

const Label = styled.div`
  font-family: var(--fonts-default);
  font-weight: 600;
  font-size: 1.05rem;
  text-align: center;
  line-height: 1.2;
  white-space: pre-line;
`;

export default function HomeNavigationTile({
  label,
  tooltip,
  to,
  defaultState,
  hoverState,
}: Props) {
  const history = useHistory();
  const [isHovered, setIsHovered] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);

  const visual = isHovered ? hoverState : defaultState;
  const baseIconSize = defaultState && defaultState.iconSize ? defaultState.iconSize : 16;
  const targetIconSize = visual && visual.iconSize ? visual.iconSize : baseIconSize;
  const iconScale = baseIconSize ? targetIconSize / baseIconSize : 1;

  const navigate = React.useCallback(async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const destination = typeof to === 'function' ? await to() : to;
      history.push(destination);
    } finally {
      setIsNavigating(false);
    }
  }, [history, isNavigating, to]);

  const onKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        navigate();
      }
    },
    [navigate]
  );

  return (
    <Tooltip2 content={tooltip} position="top">
      <TileCard
        interactive={false}
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        onClick={navigate}
        onKeyDown={onKeyDown}
        style={{
          background: `var(${visual.backgroundColorVar})`,
          color: `var(${visual.textColorVar})`,
          opacity: isNavigating ? 0.85 : 1,
          width: '100%',
          height: '100%',
          aspectRatio: '1 / 1',
        }}
        aria-label={label}
      >
        <TileInner>
          <IconWrap style={{ transform: `scale(${String(iconScale)})` }}>
            <Icon icon={visual.icon} iconSize={baseIconSize} color={visual.iconColor} />
          </IconWrap>
          <Label>{label}</Label>
        </TileInner>
      </TileCard>
    </Tooltip2>
  );
}
